import { useEffect, useRef } from 'react'

interface InstagramReelVideoPlayerProps {
  mediaUrl: string | null | undefined
  posterUrl?: string | null
  title?: string
  className?: string
  onPlaybackUnavailable?: () => void
}

function cx(...classNames: Array<string | false | null | undefined>): string {
  return classNames.filter(Boolean).join(' ')
}

export default function InstagramReelVideoPlayer({
  mediaUrl,
  posterUrl,
  title = 'Instagram reel video player',
  className,
  onPlaybackUnavailable,
}: InstagramReelVideoPlayerProps) {
  const notifiedUnavailableRef = useRef(false)
  const normalizedMediaUrl = typeof mediaUrl === 'string' ? mediaUrl.trim() : ''

  useEffect(() => {
    notifiedUnavailableRef.current = false
  }, [normalizedMediaUrl])

  const notifyPlaybackUnavailable = () => {
    if (!onPlaybackUnavailable || notifiedUnavailableRef.current) {
      return
    }
    notifiedUnavailableRef.current = true
    onPlaybackUnavailable()
  }

  if (!normalizedMediaUrl) {
    return (
      <div
        className={cx(
          'flex min-h-[240px] items-center justify-center rounded-xl border border-border-light bg-border-light/40 p-6 text-center',
          className,
        )}
      >
        <p className="text-sm font-semibold text-text-secondary">
          Reel playback is unavailable for this post.
        </p>
      </div>
    )
  }

  return (
    <div className={cx('mx-auto w-full max-w-[430px]', className)}>
      <div className="relative w-full overflow-hidden rounded-xl border border-border-light bg-card-surface shadow-md">
        <div className="w-full" style={{ aspectRatio: '9 / 16' }}>
          <video
            src={normalizedMediaUrl}
            poster={posterUrl ?? undefined}
            controls
            playsInline
            preload="metadata"
            className="h-full w-full bg-black object-contain"
            aria-label={title}
            title={title}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            onError={notifyPlaybackUnavailable}
          >
            <track kind="captions" />
          </video>
        </div>
      </div>
    </div>
  )
}
